<template lang="pug">
div(style="position: relative")
	v-overlay(v-if="$fetchState.pending" absolute)
		v-progress-linear(indeterminate)
		div.my-6.font-italic.text-center De actuele prijzen worden opgehaald.

	.mb-6(v-for="(accommodation, partnerId) in partners")
		v-data-table.variant-table(:items="accommodation.variants.length < 5 || showAllFromPartner.includes(partnerId) ? accommodation.variants : accommodation.variants.slice(0, 3)" @click:row="selectVariant" :headers="headers" disable-sort disable-pagination hide-default-footer)
			template(#header.name="{items}")
				v-img(v-if="accommodation.variants.length && accommodation.variants[0].partner.name !== 'DS'" :src="`${accommodation.variants[0].partner.image}?width=120`" :width="60")
				span(v-else) Direct te boeken op wintersport.nl

			template(#header.price="{item}")
				v-tooltip(top max-width="300")
					p We willen je een eerlijke vergelijking tussen verschillende aanbieders bieden. Omdat niet bij elke aanbieder de skipas inclusief is tellen wij uit onze database als richtprijs de hoogseizoensprijs van een skipas op basis van volwassenen op.
					template(v-slot:activator="{ on, attrs }")
						span.text-no-wrap.d-flex(v-on="on" v-bind="attrs")
							| Vergelijkprijs
							|
							v-icon.ml-1(small color="grey") info

			template(#item.name="{item}")
				.py-3
					.font-weight-bold {{item.name}}

					factline.black--text
						li(icon="users") {{$tc('accommodations_list.occupancy', item.maxOccupancy)}}
						li(icon="bed" v-if="item.bedrooms") {{$tc('accommodations_list.bedrooms', item.bedrooms)}}
					factline.black--text(v-if="item.roomsurface")
						li(icon="square-meters") {{item.roomsurface}}m²
					factline.black--text(v-if="item.servicetype !== 'roomonly'")
						li(icon="cutlery") {{$t(`accommodations-filters.${item.servicetype}`)}}
					factline.black--text(v-if="hasCancellation(item)")
						li.success--text(icon="check" vuetify-icon color="success") {{$t(`accommodations_list.cancellation`)}}

					span.mt-2(v-if="item.additionalInfo && item.additionalInfo.length")
						v-icon.mr-1(small) info
						v-tooltip(top)
							template(v-slot:activator="{ on, attrs }")
								a(v-bind="attrs" v-on="on" @click.stop) Toon meer eigenschappen
							div.ma-4
								div(v-for="(item, i) in item.additionalInfo" :key="i") {{capitalized(item)}}
					span.mt-2(v-else-if="item.description")
						v-icon.mr-1(small) info
						v-tooltip(top max-width="300")
							template(v-slot:activator="{ on, attrs }")
								a(v-bind="attrs" v-on="on" @click.stop) Toon kamerbeschrijving
							ws-content(:value="item.description")

			template(#item.skipas="{item}")
				.py-3
					.span.success--text.text-no-wrap(v-if="item.skipas && item.skipas.included")
						ws-icon(color="success" small) check
						| inclusief

					v-tooltip(top max-width="300" transition="" v-else)
						p skipas {{item.skipas.description ? `(${item.skipas.description})` : ''}} zelf ter plekke aan te schaffen: ({{$n(item.costs.find(cost => cost.code === 'SKI').amount / item.persons, 'currency', $i18n.locale)}} p.p.)
						template(#activator="{ on, attrs }")
							span.d-flex(v-on="on" v-bind="attrs")
								| {{$n(item.costs.find(cost => cost.code === 'SKI').amount, 'currency', $i18n.locale)}}
								|
								v-icon.ml-1(small color="accent") info


			template(#item.additionalCosts="{item}")
				.py-3
					v-tooltip(top v-if="item.costs.filter(cost => {return ['MAN', 'PAL'].includes(cost.type)}).length")
						div.py-2
							breakdown-cost.white--text(v-for="cost in item.costs.filter(cost => {return ['MAN', 'PAL'].includes(cost.type)})" :value="cost" :key="cost.code")
						template(v-slot:activator="{ on, attrs }")
							span.text-no-wrap.d-flex(v-on="on" v-bind="attrs")
								| {{ $n(item.costs.reduce((sum, cost) => { return ['MAN', 'PAL'].includes(cost.type) ? sum + cost.amount : sum }, 0), 'currency', $i18n.locale) }}
								|
								v-icon.ml-1(small color="accent") info
					span(v-else) -

			template(#item.accommodation="{item}")
				.py-3
					.font-weight-bold {{ $n(item.priceBreakdown.acco, 'currency', $i18n.locale) }}
					.caption ({{ $n(item.priceBreakdown.acco / item.persons, 'currency', $i18n.locale) }} p.p.)


			template(#item.price="{item}")
				.py-3
					.headline.accent--text.font-weight-bold {{ $n(Math.ceil(item.price), 'simplecurrency', $i18n.locale) }}
						//- v-tooltip(bottom color="rgba(60,60,60,0.99)")
							template(v-slot:activator="{ on, attrs }")
								v-icon(small v-on="on") info
							div.py-2
								breakdown-cost.white--text(:value="{amount: item.priceBreakdown.acco, code: 'ACC'}")
								breakdown-cost.white--text(v-for="cost in item.costs.filter(cost => {return ['MAN', 'PAL', 'SED'].includes(cost.type)})" :value="cost" :key="cost.code")
							div
								v-list-item(dense v-for="(tax, index) in item.priceBreakdown.taxes" :key="index")
									v-list-item-subtitle.flex-row.d-flex.justify-space-between.white--text
										span.d-flex {{tax.name}}
										span.d-flex.text-right.success--text(v-if="tax.included") inclusief
										span.d-flex.text-right(v-else) {{tax.percentage}}%
					.caption ({{ $n(Math.ceil(item.price/item.persons), 'simplecurrency', $i18n.locale) }} p.p.)


			template(#item.priceBreakdown="{item}")
				v-list-item.px-0(dense style="min-height:20px")
					v-list-item-subtitle.flex-row.d-flex.justify-space-between
						span.text-left Betalen aan aanbieder
						span.text-right.d-flex.ml-2  {{ $n(item.costs.reduce((sum, cost) => { return cost.type === 'MAN' ? sum + cost.amount : sum }, item.priceBreakdown.acco), 'currency', $i18n.locale) }}
				v-list-item.px-0(v-if="item.costs.some(cost => cost.type === 'PAL')" dense style="min-height:20px")
					v-list-item-subtitle.flex-row.d-flex.justify-space-between
						span.text-left Ter plaatse betalen
						span.text-right.d-flex.ml-2 {{ $n(item.costs.reduce((sum, cost) => { return cost.type === 'PAL' ? sum + cost.amount : sum }, 0), 'currency', $i18n.locale) }}
				v-list-item.px-0(v-if="item.skipas && item.skipas.included" dense style="min-height:20px")
					v-list-item-subtitle.flex-row.d-flex.justify-space-between
						span.text-left(style="white-space:normal") {{item.skipas.description}}
						span.text-right.d-flex.ml-2.success--text inclusief
				v-list-item.px-0(v-else dense style="min-height:20px")
					v-list-item-subtitle.flex-row.d-flex.justify-space-between
						span.text-left Skipas, zelf te regelen
						span.text-right.d-flex.ml-2 {{ $n(item.costs.reduce((sum, cost) => { return cost.type === 'SED' ? sum + cost.amount : sum }, 0), 'currency', $i18n.locale) }}

			template(#item.id="{item}")
				slot(name="action" v-bind:item="item")
					v-btn.gradient(color="primary" small depressed @click.stop :href="`/redir?u=${encodeURIComponent(item.url.replace(':LABEL:', 'acco'))}&p=${item.partner.id}&i=${item.id}&t=acco&h=${$redirHash(item.url.replace(':LABEL:', 'acco'))}`" target="_blank" rel="nofollow") checken

		v-divider
		template(v-if="accommodation.variants.length >= 5 && !showAllFromPartner.includes(partnerId)")
			div.text-center.mt-3
				v-btn(depressed color="primary" outlined @click="showAllFromPartner.push(partnerId)") toon alle kamers van deze aanbieder ({{accommodation.variants.length}})
	//- v-alert.caption(v-if="remainingPartners.length" dense outlined type="warning") {{accommodation.name}} wordt ook aangeboden door {{remainingPartners}} maar is niet beschikbaar in de gekozen week.

</template>

<style lang="sass">

.variant-table
	td
		vertical-align: top
	.v-btn__content
		font-size: 13px
	.itemname
		width: 40%
</style>

<script>
import gql from 'graphql-tag';

import { VListItem, VListItemSubtitle } from 'vuetify/lib';
import { gluelist } from '@/utils/gluelist';

const BreakdownCost = {
	functional: true,
	render(createElement, context) {
		const cost = context.props.value;
		if (cost.amountType === 'NOR') {
			return null;
		}
		if (cost.type === 'OPT') {
			return null;
		}
		if (cost.code === 'PET') {
			return null;
		}

		let price = context.parent.$n(
			cost.amount,
			'currency',
			context.parent.$i18n.locale
		);
		const className = ['d-flex'];

		if (cost.type === 'INC') {
			price = createElement('span', { class: 'success--text' }, ['inclusief']);
		}

		// TODO: Dit verplaatsen we nog naar locale files zodra we definitief hebben wat er echt getoond moet worden
		let description =
			{
				ACC: 'accommodatie',
				SKI: 'skipas', // TODO: de description van de skipas moet nog getoond worden ergens
				RES: 'reserveringskosten',
				CAL: 'calamiteitenfonds',
				SGR: 'SGR',
				LOC: 'toeristenbelasting',
				TOU: 'toeristenbelasting',
				DEP: 'borg',
				FIN: 'eindschoonmaak',
				ENV: 'milieubelasting',
				TWP: 'handdoekenpakket',
				BBE: 'babybed',
				CRP: 'parkeerplaats',
				BED: 'bedlinnen',
				LIN: 'bedlinnen + handdoek',
				CCH: 'kinderstoel'
			}[cost.code] || cost.code;

		if (cost.amountType === 'PPD') {
			description += `(${cost.originalAmount} p.p.p.d.)`;
		}
		if (cost.amountType === 'PPN') {
			description += `(${cost.originalAmount} p.p.p.n.)`;
		}

		if (cost.type === 'SED') {
			description += ' zelf ter plaatse kopen';
			className.push('font-weight-bold');
		}
		if (['PAL', 'XPL'].includes(cost.type)) {
			description += ' ter plaatse betalen';
		}

		if (!description.length) {
			description = JSON.stringify(cost);
		}

		return createElement(
			VListItem,
			{
				props: { dense: true },
				style: { minHeight: '20px' }
			},
			[
				createElement(
					VListItemSubtitle,
					{
						class: `flex-row d-flex justify-space-between ${context.data.staticClass}`
					},
					[
						createElement('span', { class: className }, [description]),
						createElement('span', { class: 'text-right d-flex ml-12' }, [price])
					]
				)
			]
		);
	}
};

const query = gql`
	query getAccommodationDetails($filters: String, $id: ID!) {
		accommodation(id: $id) {
			id
			name
			allPartners {
				id
				name
			}
			variants(filters: $filters) {
				id
				accommodationId
				roomId
				houseId
				name
				price
				date
				persons
				bedrooms
				roomsurface
				maxOccupancy
				duration
				servicetype
				url
				transporttype
				checkout
				additionalInfo
				description(translate: true) {
					markdown
				}
				priceBreakdown {
					acco
					skipas
					extra
					taxes {
						taxCode
						included
						percentage
						name
					}
				}
				costs {
					amount
					type
					originalAmount
					code
					amountType
				}
				partner {
					id
					name
					image
				}
				skipas {
					description
					included
				}
				policies {
					policyType
					policyRules {
						beforeArrivalAmount
					}
				}
			}
		}
	}
`;

const bookingCreateMutation = gql`
	mutation ($availabilityId: String!, $variant: BookingVariantInput) {
		bookingCreate(availabilityId: $availabilityId, variant: $variant) {
			id
		}
	}
`;

export default {
	components: {
		BreakdownCost
	},
	props: {
		accommodationId: {
			type: String,
			required: true
		},
		filterstring: {
			type: String,
			required: true
		},
		hideBreakdown: {
			type: Boolean,
			default: false
		},
		hideVariant: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			accommodation: {},
			variants: [],
			showAllFromPartner: []
		};
	},
	async fetch() {
		await this.$graphClient
			.query({
				query,
				variables: {
					filters: this.filterstring,
					id: this.accommodationId,
					context: {
						fetchAccoDetail: true,
						logMissingRooms: true
					}
				}
			})
			.then(({ data }) => {
				this.accommodation = data.accommodation;
				this.variants = data.accommodation.variants.filter(
					variant => variant.id !== this.hideVariant
				);
			});
	},
	computed: {
		sortedVariants() {
			return [...this.variants].sort((a, b) => {
				return a.price - b.price;
			});
		},
		partners() {
			return this.sortedVariants.reduce((carry, variant) => {
				if (!carry[variant.partner.id]) {
					carry[variant.partner.id] = variant.partner;
					carry[variant.partner.id].variants = [];
				}
				carry[variant.partner.id].variants.push(variant);
				return carry;
			}, {});
		},
		headers() {
			return [
				{ text: '', value: 'name', class: 'itemname' },
				{ text: 'Vergelijkprijs', value: 'price' },
				{ text: 'Skipassen', value: 'skipas' },
				{ text: 'Overige', value: 'additionalCosts' },
				{ text: 'Accommodatie', value: 'accommodation' },
				this.hideBreakdown
					? null
					: {
							text: 'Prijsopbouw',
							value: 'priceBreakdown',
							width: 300,
							class: 'breakdown',
							align: 'right'
					  },
				{ text: '', value: 'id', align: 'right', width: 50 }
			].filter(header => !!header);
		},
		remainingPartners() {
			if (!this.accommodation.allPartners) return [];
			const remainingPartners = this.accommodation.allPartners.reduce(
				(carry, partner) => {
					if (!Object.keys(this.partners).includes(partner.id))
						carry.push(partner.name);
					return carry;
				},
				[]
			);
			return remainingPartners.length
				? gluelist(remainingPartners, ', ', this.$t('global.and'))
				: [];
		}
	},
	watch: {
		filterstring: '$fetch',
		accommodationId: '$fetch',
		variants: 'variantCount'
	},
	methods: {
		selectVariant(variant) {
			this.$emit('select-variant', variant);
			if (variant.checkout === 'FRAME') {
				// snowtrex
				if (
					variant.partner.id === 'a9278f14-a852-5ae4-8f0f-f0bac4486ce6' ||
					variant.partner.id === '8690ab49-3e55-55ba-8e04-8c50c2a58507'
				)
					return this.$router.push(
						this.localePath({
							name: 'accommodations-checkout-snowtrex',
							params: { id: variant.id }
						})
					);
			} else if (variant.checkout === 'ONSITE') {
				this.$nuxt.$loading.start();

				return this.$graphClient
					.mutate({
						mutation: bookingCreateMutation,
						variables: {
							availabilityId: variant.id,
							variant: {
								persons: variant.persons,
								duration: variant.duration,
								date: variant.date,
								roomId: variant.roomId,
								accommodationId: variant.accommodationId.replace(
									/NS_|IH_|BV_/gi,
									''
								),
								milqId: this.accommodation.id,
								partnerId: variant.partner.id
							}
						},
						errorPolicy: 'all'
					})
					.then(({ data, errors }) => {
						if (!errors) {
							this.$router.push(
								this.localePath({
									name: 'accommodations-checkout',
									params: {
										id: data.bookingCreate.id
									}
								})
							);
						}
					})
					.catch(error => {
						console.log(error);
					});
			} else {
				this.$gtag.event('click', {
					event_label: `acco-go-to-provider-${variant.partner.name}`,
					event_category: `accommodations-search-id`
				});

				window.open(
					`/redir?u=${encodeURIComponent(
						variant.url.replace(':LABEL:', 'acco')
					)}&p=${variant.partner.id}&i=${variant.id}&t=acco&h=${this.$redirHash(
						variant.url.replace(':LABEL:', 'acco')
					)}`,
					'_blank'
				);
			}
		},
		variantCount() {
			this.$emit('variant-count-changed', this.variants.length);
		},
		capitalized(string) {
			return string.charAt(0).toUpperCase() + string.slice(1);
		},
		hasCancellation(variant) {
			if (!variant.policies || !variant.policies.length) return false;
			const cancellationPolicy = variant.policies.find(
				p => p.policyType === 'cancellation'
			);
			return (
				cancellationPolicy &&
				cancellationPolicy.policyRules.beforeArrivalAmount === 0
			);
		}
	}
};
</script>
